import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { BackButton, PrimaryButton } from "src/components/base/button";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceFromIpResponse } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";
import ipaddr from "ipaddr.js";
import { useForm, useStore } from "@tanstack/react-form";
import {
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
    Description,
} from "src/components/base/fieldset";
import { commitDraft, DomainChip, DomainTagInput } from "src/components/dialogs/domain-tag-input";
import { Input } from "src/components/base/input";
import { Divider } from "src/components/base/divider";
import { LocalTab, TabMenu } from "src/components/base/tab-menu";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { Text } from "src/components/base/text";
import { BadgeButton } from "src/components/base/badge";
import { LinkIcon } from "@heroicons/react/20/solid";
import Form from "src/components/base/form";
import { Heading } from "src/components/base/heading";
import { Checkbox, CheckboxField, CheckboxGroup } from "src/components/base/checkbox";

/**
 * The properties for {@link CreateDomain}
 */
export type CreateDomainProps = {};

/**
 * IP input mode, either choose a device or enter IP addresses manually
 */
type IpInputMode = "device" | "manual";

/**
 * View for creating a new domain
 */
function CreateDomain(props: CreateDomainProps) {
    const [t] = useTranslation("admin-dns-extra-records-create-domain");
    const [tg] = useTranslation();
    const router = useRouter();
    const navigate = useNavigate();
    const { ipv4Prefill, ipv6Prefill } = Route.useSearch();

    const form = useForm({
        // Errors in this form come from the server, so make sure it's possible to call "submit" even if invalid
        canSubmitWhenInvalid: true,
        defaultValues: {
            chips: [] as DomainChip[],
            chipDraft: "",
            inputMode: (ipv4Prefill !== undefined || ipv6Prefill !== undefined ? "manual" : "device") as IpInputMode,
            ipv4: ipv4Prefill || "",
            ipv6: ipv6Prefill || "",
            description: "",
            device: "",
            deviceUseV4: false,
            deviceUseV6: false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const manualV4 = value.ipv4.trim();
                const manualV6 = value.ipv6.trim();
                const deviceUseV4 = value.deviceUseV4;
                const deviceUseV6 = value.deviceUseV6;
                const inputMode = value.inputMode;

                if (inputMode === "device") {
                    if (!selectedDevice) {
                        return { fields: { device: t("error.no-device-selected") } };
                    }
                    if (!deviceUseV4 && !deviceUseV6) {
                        return { fields: { device: t("error.no-device-ips-chosen") } };
                    }
                }
                if (inputMode === "manual" && manualV4 === "" && manualV6 === "") {
                    return { fields: { ipv4: t("error.no-ip") } };
                }

                const v4 = inputMode === "manual" ? manualV4 : deviceUseV4 ? (selectedDevice?.ipv4?.trim() ?? "") : "";
                const v6 = inputMode === "manual" ? manualV6 : deviceUseV6 ? (selectedDevice?.ipv6?.trim() ?? "") : "";

                const pending = commitDraft(value.chipDraft, value.chips);
                if (pending.draft !== value.chipDraft) form.setFieldValue("chipDraft", pending.draft);
                if (pending.chips.length === 0) {
                    return { fields: { chips: t("error.no-domains") } };
                }

                const res = await ApiInternal.dns.createExtraDnsRecords({
                    description: value.description,
                    domains: pending.chips.map((chip) => chip.name),
                    ipv4_target: v4 !== "" ? v4 : null,
                    ipv6_target: v6 !== "" ? v6 : null,
                });

                if (res?.result === "Err") {
                    const err = res.error;

                    // IP address-related errors are attached to the form respective fields.
                    // In 'device mode' there are no visible IP inputs, so surface them on the device field instead.
                    const fieldErrors: Partial<Record<"ipv4" | "ipv6" | "device" | "chips", string>> = {};
                    const setIpError = (field: "ipv4" | "ipv6", msg: string) => {
                        if (inputMode === "manual") fieldErrors[field] = msg;
                        else fieldErrors.device = msg;
                    };
                    if (err.no_ip_configured) setIpError("ipv4", t("error.no-ip"));
                    if (err.ipv4_invalid) setIpError("ipv4", t("error.invalid-ip"));
                    if (err.no_internal_ipv4_device) setIpError("ipv4", t("error.no-internal-device"));
                    if (err.ipv6_invalid) setIpError("ipv6", t("error.invalid-ip"));
                    if (err.no_internal_ipv6_device) setIpError("ipv6", t("error.no-internal-device"));

                    // Per-domain errors are attached to the chips. A domain may appear in
                    // multiple error lists, so all matching messages are combined.
                    const invalidDomains = new Set(err.invalid_domains);
                    const collidingDomains = new Set(err.domains_collide_existing_domains);
                    const collidingDevices = new Set(err.domains_collide_existing_devices);

                    const newChips: DomainChip[] = pending.chips.map((chip) => {
                        const messages: string[] = [];
                        if (invalidDomains.has(chip.name)) messages.push(t("error.invalid-domain"));
                        if (collidingDomains.has(chip.name)) messages.push(t("error.domain-exists"));
                        if (collidingDevices.has(chip.name)) messages.push(t("error.device-exists"));

                        if (messages.length > 0) {
                            return { ...chip, status: "error", error: messages.join("\n") };
                        }
                        return chip.status === "error" ? { ...chip, status: "pending", error: undefined } : chip;
                    });

                    form.setFieldValue("chips", newChips);

                    // Always return a non-empty error in this branch
                    return Object.keys(fieldErrors).length > 0 ? { fields: fieldErrors } : t("error.some-failed");
                }

                await router.invalidate();
                await navigate({ to: "/a/dns/extra-records", search: { search: "" } });
            },
        },
    });

    useEffect(() => {
        form.reset();
        setSelectedDevice(undefined);
    }, []);

    const formInputMode = useStore(form.store, (s) => s.values.inputMode);
    const formIpv4 = useStore(form.store, (s) => s.values.ipv4);
    const formIpv6 = useStore(form.store, (s) => s.values.ipv6);
    const v4Device = useDeviceForIp(formIpv4);
    const v6Device = useDeviceForIp(formIpv6);

    const formDevice = useStore(form.store, (s) => s.values.device);
    const [selectedDevice, setSelectedDevice] = useState<ShortDevice | undefined>(undefined);
    const searchedDevices = useFindServersByName(formDevice);

    const deviceTabRef = React.useRef<HTMLDivElement>(null);
    const manualTabRef = React.useRef<HTMLDivElement>(null);

    /**
     * Select an IP input mode and move keyboard focus to its tab
     *
     * @param mode new selected IP selection mode
     */
    function selectMode(mode: IpInputMode) {
        form.setFieldValue("inputMode", mode);
        (mode === "device" ? deviceTabRef : manualTabRef).current?.focus();
    }

    /**
     * Keyboard navigation for the mode tabs (roving tabindex, selection follows focus)
     *
     * @param e keyboard event from the tab menu
     */
    function handleModeTabKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        switch (e.key) {
            case "ArrowLeft":
            case "ArrowRight":
                e.preventDefault();
                selectMode(formInputMode === "device" ? "manual" : "device");
                break;
            case "Home":
                e.preventDefault();
                selectMode("device");
                break;
            case "End":
                e.preventDefault();
                selectMode("manual");
                break;
            case "Enter":
            case " ":
                // Selection already follows focus, but re-apply in case they diverge
                e.preventDefault();
                selectMode(e.currentTarget === deviceTabRef.current ? "device" : "manual");
                break;
        }
    }

    return (
        <div className={"flex flex-col gap-6"}>
            <BackButton className={"self-start"} onClick={() => history.back()}>
                {tg("button.go-back")}
            </BackButton>

            <Form onSubmit={form.handleSubmit} className={"max-w-2xl"}>
                <Fieldset>
                    <Heading>{t("heading.add-extra-domains")}</Heading>

                    <FieldGroup>
                        <form.Field name={"chips"}>
                            {(chipsField) => (
                                <form.Field name={"chipDraft"}>
                                    {(draftField) => (
                                        <Field>
                                            <RequiredLabel>{t("label.domains")}</RequiredLabel>
                                            <Description>{t("description.domains-input")}</Description>
                                            <DomainTagInput
                                                chips={chipsField.state.value}
                                                onChange={(c) => {
                                                    chipsField.handleChange(c);
                                                    chipsField.setMeta((meta) => ({ ...meta, errors: [] }));
                                                }}
                                                draft={draftField.state.value}
                                                onDraftChange={(d) => {
                                                    draftField.handleChange(d);
                                                    chipsField.setMeta((meta) => ({ ...meta, errors: [] }));
                                                }}
                                                placeholder={t("placeholder.first-domain")}
                                                removeAriaLabel={t("button.remove-domain")}
                                                autoFocus={true}
                                            />
                                            {chipsField.state.meta.errors.map((err) => (
                                                <ErrorMessage key={String(err)}>{err}</ErrorMessage>
                                            ))}
                                        </Field>
                                    )}
                                </form.Field>
                            )}
                        </form.Field>

                        <form.Field name={"description"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.description")}</Label>
                                    <Input
                                        maxLength={255}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                        <Divider />

                        <TabMenu>
                            <span className={"mr-2 self-center text-sm/5 text-zinc-600 dark:text-zinc-400"}>
                                {t("label.select-target")}
                            </span>
                            <div
                                ref={deviceTabRef}
                                className={"relative"}
                                tabIndex={formInputMode === "device" ? 0 : -1}
                                onKeyDown={handleModeTabKeyDown}
                            >
                                <LocalTab active={formInputMode === "device"} onClick={() => selectMode("device")}>
                                    {t("label.choose-device")}
                                </LocalTab>
                            </div>
                            <div
                                ref={manualTabRef}
                                className={"relative"}
                                tabIndex={formInputMode === "manual" ? 0 : -1}
                                onKeyDown={handleModeTabKeyDown}
                            >
                                <LocalTab active={formInputMode === "manual"} onClick={() => selectMode("manual")}>
                                    {t("label.enter-manually")}
                                </LocalTab>
                            </div>
                        </TabMenu>

                        {formInputMode === "device" && (
                            <>
                                <form.Field name={"device"}>
                                    {(fieldApi) => (
                                        <Field>
                                            <Label>{t("label.choose-device")}</Label>
                                            <Combobox<ShortDevice | string>
                                                className={"order-3 max-lg:col-span-2 lg:order-6"}
                                                value={fieldApi.state.value}
                                                onChange={(e) => {
                                                    if (e && typeof e === "object") {
                                                        fieldApi.handleChange(e.name ?? "");
                                                        setSelectedDevice(e);
                                                    } else {
                                                        fieldApi.handleChange(e ?? "");
                                                    }
                                                }}
                                                onClose={() => {}}
                                                invalid={fieldApi.state.meta.errors.length > 0}
                                                placeholder={t("label.enter-device-name")}
                                                queryDisplay={(q) => (typeof q === "object" && q ? q.name : (q ?? ""))}
                                                onQueryChange={(q) => {
                                                    fieldApi.handleChange(q);
                                                    setSelectedDevice(undefined);
                                                }}
                                            >
                                                {(searchedDevices ?? []).map((device) => (
                                                    <ComboboxOption value={device} key={device.uuid}>
                                                        <div className="flex w-full items-center justify-between">
                                                            <span className="mr-3">{device.name}</span>
                                                            <span className="text-xs">{device.ipv4}</span>
                                                        </div>
                                                    </ComboboxOption>
                                                ))}
                                            </Combobox>
                                            {fieldApi.state.meta.errors.map((err) => (
                                                <ErrorMessage key={String(err)}>{err}</ErrorMessage>
                                            ))}
                                        </Field>
                                    )}
                                </form.Field>
                                {selectedDevice && (
                                    <>
                                        <form.Field name={"deviceUseV4"}>
                                            {(fieldApi) => (
                                                <Field>
                                                    <Label>{t("label.use-device-v4")}</Label>
                                                    <CheckboxGroup>
                                                        <CheckboxField>
                                                            <Checkbox
                                                                color={"blue"}
                                                                checked={fieldApi.state.value}
                                                                onChange={(e) => fieldApi.handleChange(e)}
                                                            />
                                                            <Label>{selectedDevice.ipv4}</Label>
                                                        </CheckboxField>
                                                    </CheckboxGroup>
                                                </Field>
                                            )}
                                        </form.Field>
                                        <form.Field name={"deviceUseV6"}>
                                            {(fieldApi) => (
                                                <Field>
                                                    <Label>{t("label.use-device-v6")}</Label>
                                                    <CheckboxGroup>
                                                        <CheckboxField>
                                                            <Checkbox
                                                                color={"blue"}
                                                                checked={fieldApi.state.value}
                                                                onChange={(e) => fieldApi.handleChange(e)}
                                                            />
                                                            <Label>{selectedDevice.ipv6}</Label>
                                                        </CheckboxField>
                                                    </CheckboxGroup>
                                                </Field>
                                            )}
                                        </form.Field>
                                    </>
                                )}
                            </>
                        )}

                        {formInputMode === "manual" && (
                            <>
                                <Text className={"text-xs"}>{t("description.ips-hint")}</Text>
                                <form.Field name={"ipv4"}>
                                    {(fieldApi) => (
                                        <Field>
                                            <Label>{t("label.ipv4")}</Label>
                                            <Input
                                                maxLength={255}
                                                invalid={fieldApi.state.meta.errors.length > 0}
                                                value={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                placeholder={"192.0.2.1"}
                                            />
                                            {v4Device && (
                                                <BadgeButton
                                                    className={"mt-2"}
                                                    color={"zinc"}
                                                    target={"_blank"}
                                                    href={"/a/devices/$deviceId/general"}
                                                    params={{ deviceId: v4Device.uuid }}
                                                >
                                                    <LinkIcon className={"w-4"} />
                                                    <span> {v4Device.name}</span>
                                                </BadgeButton>
                                            )}
                                            {fieldApi.state.meta.errors.map((err) => (
                                                <ErrorMessage key={String(err)}>{err}</ErrorMessage>
                                            ))}
                                        </Field>
                                    )}
                                </form.Field>
                                <form.Field name={"ipv6"}>
                                    {(fieldApi) => (
                                        <Field>
                                            <Label>{t("label.ipv6")}</Label>
                                            <Input
                                                maxLength={255}
                                                invalid={fieldApi.state.meta.errors.length > 0}
                                                value={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                placeholder={"2001:db8::1"}
                                            />
                                            {v6Device && (
                                                <BadgeButton
                                                    className={"mt-2"}
                                                    color={"zinc"}
                                                    target={"_blank"}
                                                    href={"/a/devices/$deviceId/general"}
                                                    params={{ deviceId: v6Device.uuid }}
                                                >
                                                    <LinkIcon className={"w-4"} />
                                                    <span> {v6Device.name}</span>
                                                </BadgeButton>
                                            )}
                                            {fieldApi.state.meta.errors.map((err) => (
                                                <ErrorMessage key={String(err)}>{err}</ErrorMessage>
                                            ))}
                                        </Field>
                                    )}
                                </form.Field>
                            </>
                        )}
                    </FieldGroup>
                </Fieldset>

                <Divider className={"my-8"} />
                <form.Subscribe
                    selector={(state) => state.errorMap.onSubmit}
                    children={(error) =>
                        error ? (
                            <p className="my-8 text-base/6 font-medium text-red-600 data-disabled:opacity-50 sm:text-sm/6 dark:text-red-500">
                                {error.toString()}
                            </p>
                        ) : null
                    }
                />
                <PrimaryButton type={"submit"}>{t("button.add")}</PrimaryButton>
            </Form>
        </div>
    );
}

/**
 * Detect the IP address version for valid IP addresses
 *
 * @param value any text to verify it
 * @returns IP version or null if it's not an IP address
 */
function isIpAddress(value: string): "ipv4" | "ipv6" | null {
    try {
        const parsed = ipaddr.parse(value.trim());
        return parsed.kind();
    } catch {
        return null;
    }
}

/**
 * Short form of a `FullDevice` limited to the data needed for this form
 */
type ShortDevice = {
    uuid: string;
    ipv4?: string | null;
    ipv6?: string | null;
    name: string; // DNS name
};

/**
 * Looks up the top 5 server devices using a name filter, debounced. Returns list of found devices.
 *
 * @param name the name of a device that is looked up
 * @returns the matching devices, possibly empty list
 */
function useFindServersByName(name: string): Array<ShortDevice> | undefined {
    const [devices, setDevices] = useState<Array<ShortDevice>>();
    useEffect(() => {
        if (name.trim().length < 2) {
            setDevices([]);
            return;
        }
        const timer = setTimeout(async () => {
            setDevices((await ApiInternal.devices.getFromNames(name)).devices);
        }, 500);
        return () => clearTimeout(timer);
    }, [name]);
    return devices;
}

/**
 * Looks up the internal device that owns `ip`, debounced. Returns `undefined` for empty input
 * or if no device matches the address.
 *
 * @param ip the IP address to resolve to a device
 * @returns the matching device, or `undefined`
 */
function useDeviceForIp(ip: string): DeviceFromIpResponse | undefined {
    const [device, setDevice] = useState<DeviceFromIpResponse | undefined>();
    useEffect(() => {
        if (ip.trim() === "" || isIpAddress(ip) === null) {
            setDevice(undefined);
            return;
        }
        const t = setTimeout(async () => {
            setDevice(await ApiInternal.devices.getFromIp(ip));
        }, 500);
        return () => clearTimeout(t);
    }, [ip]);
    return device;
}

/**
 * URL search params for the 'create extra domain', to pre-fill the IPv4 and IPv6
 */
type CreateExtraDomainSearch = {
    ipv4Prefill?: string;
    ipv6Prefill?: string;
};

export const Route = createFileRoute("/_admin/a/dns/_dns/extra-records/create-new-domain")({
    validateSearch: (search: Record<string, unknown>): CreateExtraDomainSearch => {
        return {
            ipv4Prefill: typeof search.ipv4Prefill === "string" ? search.ipv4Prefill : undefined,
            ipv6Prefill: typeof search.ipv6Prefill === "string" ? search.ipv6Prefill : undefined,
        };
    },
    loaderDeps: ({ search }) => ({ ipv4Prefill: search.ipv4Prefill, ipv6Prefill: search.ipv6Prefill }),
    component: CreateDomain,
});
